
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, AlertTriangle, Users, Clock } from 'lucide-react';
import { toast } from 'sonner';

const EmergencyRequest: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [situation, setSituation] = useState('');
  const [searchRadius, setSearchRadius] = useState(5);

  const handleEmergencyRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!address) {
      toast.error('Please enter an address');
      return;
    }
    
    setLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success('Emergency request sent', {
        description: 'Nearby volunteers have been notified of the situation.'
      });
      
      navigate('/emergency-confirmation');
    } catch (error) {
      toast.error('Failed to send emergency request', {
        description: 'Please try again or call emergency services directly.'
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Layout>
      <Helmet>
        <title>Emergency Request | CareConnect</title>
        <meta name="description" content="Request immediate assistance from nearby volunteers for elderly individuals in need of help." />
      </Helmet>
      
      <section className="pt-32 pb-20 bg-gradient-to-br from-care-50 to-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-8"
            >
              <span className="inline-block py-1 px-3 rounded-full text-sm font-medium bg-red-100 text-red-800 mb-4">
                Emergency Assistance
              </span>
              
              <h1 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-balance">
                Request volunteer check-in
              </h1>
              
              <p className="text-lg text-muted-foreground mb-6 text-balance">
                Alert nearby volunteers to check on your loved one in case of emergency or concern.
              </p>
            </motion.div>
            
            <Alert variant="destructive" className="mb-6">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                For medical emergencies or immediate danger, call 911 first. This service is for non-life-threatening situations where a welfare check is needed.
              </AlertDescription>
            </Alert>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="glass-morphism rounded-xl p-6 mb-8"
            >
              <form onSubmit={handleEmergencyRequest} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="address">Address to check</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-care-500 h-5 w-5" />
                    <Input
                      id="address"
                      placeholder="Enter full address"
                      className="pl-10"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="radius">Search radius (miles)</Label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="range"
                      id="radius"
                      min="1"
                      max="20"
                      step="1"
                      value={searchRadius}
                      onChange={(e) => setSearchRadius(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <span className="text-care-700 font-medium w-8">{searchRadius}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="situation">Situation description</Label>
                  <Textarea
                    id="situation"
                    placeholder="Briefly describe the reason for the check-in request"
                    value={situation}
                    onChange={(e) => setSituation(e.target.value)}
                    className="min-h-[120px]"
                    required
                  />
                </div>
                
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-3">
                  <div className="flex items-start">
                    <Users className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Volunteer network</p>
                      <p className="text-xs text-blue-700">There are 8 volunteers currently active within {searchRadius} miles of this location</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Clock className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Estimated response time</p>
                      <p className="text-xs text-blue-700">Most volunteers respond within 5-15 minutes</p>
                    </div>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  disabled={loading}
                >
                  {loading ? 'Sending Request...' : 'Send Emergency Check-in Request'}
                </Button>
              </form>
            </motion.div>
            
            <div className="text-center text-sm text-muted-foreground">
              <p>By using this service, you agree to our terms and community guidelines. <br />Misuse of the emergency system may result in account restrictions.</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EmergencyRequest;
